import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { BotaoAcao, CampoBusca, CardResumo, Etiqueta, Pagina, celula, celulaCabecalho } from "./ui/Painel";
import { CORES, normalizar } from "../utils/participante";

// A distrital interna da inscricao publica e criada com 999999 vagas.
const NOME_DISTRITAL_PUBLICA = "Distrital Inscricao Publica";
const semLimite = (d) => d.nome === NOME_DISTRITAL_PUBLICA || d.limite_participantes >= 99999;
const nomeExibicao = (d) => (d.nome === NOME_DISTRITAL_PUBLICA ? "Inscrição pública" : d.nome);

const ListagemDistritais = () => {
  const navigate = useNavigate();
  const [distritais, setDistritais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [busca, setBusca] = useState("");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/distrital/listar`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error("Erro ao buscar distritais");
        return res.json();
      })
      .then((data) => setDistritais(data))
      .catch((error) => setErro(error.message))
      .finally(() => setLoading(false));
  }, []);

  const resumo = useMemo(() => {
    const comVaga = distritais.filter((d) => d.limite_participantes > 0).length;
    const vagas = distritais
      .filter((d) => !semLimite(d))
      .reduce((total, d) => total + Math.max(Number(d.limite_participantes) || 0, 0), 0);
    return { total: distritais.length, comVaga, semVaga: distritais.length - comVaga, vagas };
  }, [distritais]);

  const visiveis = distritais
    .filter((d) => normalizar(nomeExibicao(d)).includes(normalizar(busca).trim()))
    .sort((a, b) => nomeExibicao(a).localeCompare(nomeExibicao(b), "pt-BR"));

  return (
    <Pagina
      eyebrow="Cadastros"
      titulo="Distritais e vagas"
      acoes={
        <BotaoAcao primario icon={<AddIcon />} onClick={() => navigate("/register-distrital")}>
          Nova distrital
        </BotaoAcao>
      }
    >
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 2.5 }}>
        <CardResumo titulo="Distritais" valor={resumo.total} detalhe="cadastradas" />
        <CardResumo titulo="Com vagas" valor={resumo.comVaga} />
        <CardResumo titulo="Sem vagas" valor={resumo.semVaga} />
        <CardResumo titulo="Vagas disponíveis" valor={resumo.vagas} detalhe="no total" />
      </Box>

      <Box sx={{ backgroundColor: "#fff", border: `1px solid ${CORES.borda}`, borderRadius: "10px", overflow: "hidden" }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${CORES.borda}` }}>
          <CampoBusca value={busca} onChange={setBusca} placeholder="Buscar distrital" sx={{ maxWidth: 360 }} />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={34} sx={{ color: CORES.navy }} />
          </Box>
        ) : erro ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {erro}
          </Alert>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...celulaCabecalho, pl: 2 }}>Distrital</TableCell>
                  <TableCell sx={celulaCabecalho}>Situação</TableCell>
                  <TableCell sx={{ ...celulaCabecalho, textAlign: "right", pr: 2 }}>Vagas disponíveis</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visiveis.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ ...celula, textAlign: "center", py: 5, color: CORES.textoSuave }}>
                      Nenhuma distrital encontrada.
                    </TableCell>
                  </TableRow>
                )}
                {visiveis.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell sx={{ ...celula, pl: 2, fontWeight: 600 }}>{nomeExibicao(d)}</TableCell>
                    <TableCell sx={celula}>
                      {semLimite(d) ? (
                        <Etiqueta tom="azul">Sem limite</Etiqueta>
                      ) : d.limite_participantes > 0 ? (
                        <Etiqueta tom="verde">Com vagas</Etiqueta>
                      ) : (
                        <Etiqueta tom="vermelho">Esgotada</Etiqueta>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ ...celula, textAlign: "right", pr: 2, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                    >
                      {semLimite(d) ? "—" : d.limite_participantes}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${CORES.borda}`, fontSize: 11.5, color: CORES.textoSuave }}>
          Mostrando {visiveis.length} de {resumo.total} distritais
        </Box>
      </Box>
    </Pagina>
  );
};

export default ListagemDistritais;
